import { AccessTime as ExpectedIcon } from '@mui/icons-material';
import {
  type PlayerTransfer,
  RankingType,
  TransferEligibility,
} from '@on3/api';
import { On3IconLockPremium } from '@on3/icons';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { Check } from '@on3/ui-lib/components/Svg';
import { useAuth } from '@on3/ui-lib/index';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { EnteredIcon } from '@public/components/Icons/EnteredIcon';
import { UndecidedIcon } from '@public/components/Icons/UndecidedIcon';
import { WithdrawnIcon } from '@public/components/Icons/WithdrawnIcon';
import { TeamAvatar } from '@public/components/Image/TeamAvatar';
import { getAbbreviatedRank } from '@public/components/Nil/helpers';
import { ArrowRight } from '@public/components/Svg/ArrowRight';
import { AthleteVerified } from '@public/components/UI/AthleteVerified/AthleteVerified';
import { TextLink } from '@public/components/UI/Link/TextLink';
import { StarRating } from '@public/components/UI/StarRating/StarRating';
import { Text } from '@public/components/UI/Typography/Text';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useRouter } from 'next/router';

import { TransferPortalArticle } from './TransferPortalArticle';
import styles from './TransferPortalItem.module.scss';

interface ITransferPortalItemProps extends PlayerTransfer {
  transferYear?: number | null;
  isIndustryRankable?: boolean;
  page?: number;
}
export const TransferPortalItem = ({
  eligibility,
  lastTeam,
  positionAbbreviation,
  recruitmentKey,
  name,
  slug,
  index,
  page,
  highSchool,
  homeTownName,
  transferYear,
  classYear,
  defaultAsset,
  height,
  weight,
  rating,
  transferIndustryComparison,
  transferRating,
  commitStatus,
  predictions,
  nilStatus,
  nilValue,
  athleteVerified,
  enteredArticle,
  committedArticle,
  isIndustryRankable,
}: ITransferPortalItemProps) => {
  const router = useRouter();

  const { transferType, sport } = router.query;

  const { user } = useAuth();
  const isWire = transferType?.toString() === 'wire';
  const isTop = transferType?.toString() === 'top';
  const isIndustry = transferType?.toString() === 'industry';
  const showIndustryRating = (isIndustry || isWire) && isIndustryRankable;
  const selectedRating = showIndustryRating
    ? transferIndustryComparison?.find((r) => r.type === 'Industry')
    : transferRating;
  const hasStatus =
    commitStatus?.type === 'Signed' ||
    commitStatus?.type === 'Enrolled' ||
    commitStatus?.type === 'Committed';
  const hasEntered = commitStatus?.type === 'Entered';
  const hasWithdrawn = commitStatus?.type === 'Withdrawn';
  const isExpectedToEnter = commitStatus?.type === 'Expected';
  const isSoftball = sport === 'softball';
  const hasPredictions = predictions ? predictions?.length > 0 : false;
  const newTeamLocked = isWire && isSoftball && !user?.has && hasEntered;
  const showCommittedOrg = (hasStatus || hasWithdrawn) && !newTeamLocked;
  const showPredictions = hasEntered && hasPredictions && !newTeamLocked;
  const showUndecided =
    (hasEntered || isExpectedToEnter) && !hasPredictions && !newTeamLocked;
  const isEnrolledOrSigned =
    commitStatus?.type === 'Signed' || commitStatus?.type === 'Enrolled';

  const portalArticle = (hasEntered && enteredArticle) || committedArticle;
  const showPlayerRank = isTop || isIndustry;

  const joinLink = isSoftball ? '/softball/join/' : '/subscribe/';
  const utm_campaign = isTop ? 'top' : 'wire';
  const utmParams = new URLSearchParams({
    utm_source: 'transfer-portal',
    utm_medium: 'web',
    utm_campaign,
    utm_content: 'lock',
  });

  const utmLink = `${joinLink}?${utmParams}`;

  return (
    <div
      className={clsx(styles.transferPortalItem, { [styles.isWire]: isWire })}
    >
      {showPlayerRank && (
        <div
          className={clsx(styles.rankContainer, {
            [styles.isConsensus]: !isIndustry,
          })}
        >
          {transferRating?.consensusNationalRank && (
            <Text
              className={clsx(styles.consensusOverallRank, {
                [styles.active]: isIndustry,
              })}
              component="span"
            >
              {transferRating.consensusNationalRank}
            </Text>
          )}
          {transferRating?.nationalRank && (
            <Text
              className={clsx(styles.overallRank, {
                [styles.active]: !isIndustry,
              })}
              component="span"
            >
              {transferRating.nationalRank}
            </Text>
          )}
        </div>
      )}
      <figure
        className={clsx(styles.playerAvatar, { [styles.isWire]: isWire })}
      >
        <Avatar
          altText={defaultAsset?.altText}
          domain={defaultAsset?.domain}
          fit="cover"
          height={90}
          quality={95}
          source={defaultAsset?.source}
          width={90}
        />
      </figure>
      <Text
        className={clsx(styles.position, { [styles.isWire]: isWire })}
        component="span"
      >
        {positionAbbreviation}
      </Text>
      <div className={styles.playerDetailsContainer}>
        <div className={styles.playerNameContainer}>
          <Link href={`/rivals/${slug}/`} useSiteColor>
            {name}
          </Link>
          {athleteVerified && <AthleteVerified />}
        </div>
        <div className={styles.playerVitalsContainer}>
          <Text className={styles.classYear} component="span">
            {commitStatus?.classRank
              ? getAbbreviatedRank(commitStatus?.classRank)
              : '-'}
          </Text>

          <Text className={styles.height} component="span">
            {height}
          </Text>

          <Text className={styles.weight} component="span">
            {weight}
          </Text>
        </div>
        <div className={styles.hometownContainer}>
          <Link
            className={styles.highSchool}
            href={`/high-school/${highSchool?.urlSlug}/`}
          >
            {highSchool?.name}
          </Link>

          <Text className={styles.homeTown} component="span">
            {homeTownName}
          </Text>
        </div>
      </div>
      <div className={styles.ratingsContainer}>
        <div className={styles.collegeRatingContainer}>
          <StarRating
            hasBorder={false}
            href={`/rivals/${slug}/industry-comparison/`}
            isBoldRating={false}
            isConsensus={showIndustryRating}
            position={selectedRating?.positionAbbr}
            rankingType={RankingType.TransferPortal}
            rating={selectedRating?.rating}
            sport={`${sport}`}
            stars={selectedRating?.stars}
            state={selectedRating?.stateAbbr}
            year={`${classYear}`}
          />
        </div>
        <StarRating
          hasBorder={false}
          href={`/rivals/${slug}/industry-comparison/`}
          isBoldRating={false}
          isConsensus
          position={rating?.positionAbbr}
          rating={rating?.rating}
          small
          stars={rating?.stars}
          state={rating?.stateAbbr}
          year={`${classYear}`}
        />
      </div>
      <div className={styles.nilContainer}>
        <Text className={styles.nilLabel}>NIL Value:</Text>
        <div className={styles.nilValuation}>
          {nilValue ? (
            formatCurrency(nilValue)
          ) : user?.has ? (
            '-'
          ) : (
            <Link href={utmLink}>
              <On3IconLockPremium />
            </Link>
          )}
        </div>
      </div>
      <div
        className={clsx(styles.playerStatusContainer, {
          [styles.isWire]: isWire,
        })}
      >
        {eligibility && eligibility !== TransferEligibility.Immediate && (
          <div
            className={clsx(styles.eligibilityContainer, {
              [styles.isWire]: isWire,
            })}
          >
            <Text className={styles.eligibilityLabel} component="span">
              Eligibility
            </Text>
            <Text className={styles.eligibilityText} component="span">
              {eligibility}
            </Text>
          </div>
        )}
        <div
          className={clsx(styles.playerStatusItem, {
            [styles.expectedStatus]: isExpectedToEnter,
          })}
        >
          {isEnrolledOrSigned && (
            <div className={styles.checkmarkContainer}>
              <Check className={styles.checkmarkIcon} />
            </div>
          )}
          {(hasEntered || hasWithdrawn || isExpectedToEnter) && (
            <div className={styles.statusIconContainer}>
              {hasEntered && <EnteredIcon className={styles.statusIcon} />}
              {hasWithdrawn && <WithdrawnIcon className={styles.statusIcon} />}
              {isExpectedToEnter && (
                <ExpectedIcon className={styles.statusIcon} />
              )}
            </div>
          )}
          <span
            className={clsx(styles.statusLabel, {
              [styles.signed]: isEnrolledOrSigned,
              [styles.withdrawn]: hasWithdrawn,
            })}
          >
            {commitStatus?.type}
          </span>
        </div>

        {commitStatus?.date && hasEntered && (
          <Text className={styles.statusDate}>
            {format(new Date(commitStatus.date), 'M/dd/yyyy')}
          </Text>
        )}
      </div>
      <div
        className={clsx(styles.teamStatusContainer, {
          [styles.isWire]: isWire,
        })}
      >
        <div className={styles.lastTeamWrapper}>
          <TextLink
            className={styles.lastTeam}
            href={`/college/${lastTeam?.slug}/${sport}/${transferYear}/transfers/`}
            underline="none"
          >
            <TeamAvatar
              className={styles.teamLogo}
              height={20}
              slug={lastTeam?.name}
              src={lastTeam?.assetUrl?.url}
              width={20}
            />
          </TextLink>
          <ArrowRight className={styles.arrowForward} stroke="#1d252c" />
        </div>
        <div>
          {showCommittedOrg && (
            <Link
              className={styles.committedLogoContainer}
              href={`/college/${commitStatus.committedOrganization?.slug}/${sport}/${transferYear}/transfers/`}
            >
              <TeamAvatar
                className={styles.teamLogo}
                height={20}
                slug={commitStatus.committedOrganization?.name}
                src={commitStatus?.committedOrganization?.assetUrl}
                width={20}
              />
            </Link>
          )}
          {showPredictions && (
            <Link
              className={styles.predictionsContainer}
              href={`/rivals/${slug}/recruiting/${recruitmentKey}/rpm/`}
            >
              {predictions?.slice(0, 2).map((org) => {
                const orgDistance = org?.distance ?? -1;
                const distanceText =
                  orgDistance > -1 ? Math.round(orgDistance) : '-';

                return (
                  <div className={styles.prediction} key={org.key}>
                    {!!org.percent && <span>{org.percent.toFixed(1)}%</span>}
                    <TeamAvatar
                      className={styles.teamLogo}
                      height={20}
                      slug={org?.organization?.name}
                      src={org?.organization?.assetUrl?.url}
                      width={20}
                    />
                    <span className={styles.rpmDistance}>
                      {distanceText}

                      <span aria-label="Miles">mi</span>
                    </span>
                  </div>
                );
              })}
            </Link>
          )}
          {showUndecided && (
            <div className={styles.undecidedContainer}>
              <UndecidedIcon className={styles.teamLogo} />
            </div>
          )}
          {newTeamLocked && (
            <Link className={styles.newTeamLock} href={utmLink}>
              <On3IconLockPremium />
            </Link>
          )}
        </div>
      </div>
      {isWire && portalArticle && (
        <div className={styles.article}>
          <TransferPortalArticle {...portalArticle} />
        </div>
      )}
    </div>
  );
};
